import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useMapCaches }     from './composables/useMapCaches.js'
import { useMapZoom }       from './composables/useMapZoom.js'
import { useMapColoring }   from './composables/useMapColoring.js'
import { useMapData }       from './composables/useMapData.js'
import { useMapHover }      from './composables/useMapHover.js'
import { useMapMarkers }    from './composables/useMapMarkers.js'
import { useMapNavigation } from './composables/useMapNavigation.js'
import { useAccessibilityList }      from './composables/useAccessibilityList.js'
import { getCountryLevelAttr, formatEntityLabel } from './helpers/DanmarksKortMap.helpers.js'
import { buildLegendIndex } from './helpers/DanmarksKortMap.legend.js'

export default {
  name: 'DanmarksKortMap',
  props: {
    title:     { type: String, default: '' },
    flow:      { type: String, default: 'municipality' },
    groupData: { type: Array,  default: () => [] },
    svgUrl:    { type: String, default: '/maps/danmark-kort-complete.svg' },
    municipalityDataUrl: { type: String, default: '/api/map-data-{key}.json' },
    accessibilityListLabelMunicipality: { type: String, default: 'Vælg kommune' },
    accessibilityListLabelSundhedsraad: { type: String, default: 'Vælg sundhedsråd' },
    legendLabel:               { type: String, default: 'Signaturforklaring' },
    srTab:                     { type: String, default: 'sundhedsraad' },
    legend: {
      type: Array,
      default: () => [
        { value: 1, label: 'Value 1' },
        { value: 2, label: 'Value 2' },
        { value: 3, label: 'Value 3' },
        { value: 4, label: 'Value 4' },
        { value: 5, label: 'Value 5' }
      ]
    }
  },
  emits: ['navigate', 'load-error'],
  setup(props, { emit, expose }) {
    const rootRef    = ref(null)
    const wrapperRef = ref(null)
    const svgContent = ref('')
    const isLoading  = ref(true)

    const mapLevel = ref('country')

    const { caches, buildCaches } = useMapCaches(wrapperRef)
    const { markers, computeMarkers, clearMarkers, observeResize } = useMapMarkers(wrapperRef, caches, mapLevel)
    const { animateViewBox, zoomTargetFor } = useMapZoom(wrapperRef, caches, { computeMarkers, clearMarkers })
    const { applyColoringByAttr, applyColoringFromLegend, greyExcept, legendEntries: baseLegendEntries } = useMapColoring(caches, props)
    const { regionDataCache, fetchRegionData, prefetchRegionData } = useMapData(props)

    const { tooltip, hoveredMarkerKey, setHover, clearHover, onMouseover, onMousemove, onMouseleave, onMarkerMouseleave, showTooltipAtSvgCentroid } =
      useMapHover(wrapperRef, caches, props, { mapLevel, prefetchRegionData })

    const { breadcrumb, activeRegionGroups, kommuneView, paintSundhedsraadTab, emitState, selectGroup, goBack, selectByName, selectRegionGroup, onMapClick } =
      useMapNavigation(props, emit, caches, mapLevel, rootRef, { animateViewBox, zoomTargetFor, applyColoringByAttr, applyColoringFromLegend, greyExcept, fetchRegionData, regionDataCache, clearHover })

    const legendEntries = computed(() => kommuneView.value ? buildLegendIndex(kommuneView.value.legend).entries : baseLegendEntries)
    const paletteStyle  = computed(() => kommuneView.value?.colorVariables ?? null)

    watch(() => props.srTab, paintSundhedsraadTab)

    const { regionListRef, regionListLabel, regionListTabIndexes, onRegionListItemFocus, onRegionListItemBlur, onRegionListKeydown } =
      useAccessibilityList(props, { activeRegionGroups, caches, setHover, clearHover, showTooltipAtSvgCentroid, goBack, tooltip })

    onMounted(async () => {
      try {
        const res = await fetch(props.svgUrl)
        if (!res.ok) throw new Error(`SVG ${res.status}`)
        svgContent.value = await res.text()
        await nextTick()
        buildCaches()
        applyColoringByAttr(getCountryLevelAttr(props.flow), props.groupData)
        computeMarkers()
        emitState()
        observeResize()
      } catch (err) {
        emit('load-error', err)
      } finally {
        isLoading.value = false
      }
    })

    watch(() => props.groupData, (data) => nextTick(() => {
      if (mapLevel.value === 'country') applyColoringByAttr(getCountryLevelAttr(props.flow), data)
    }))

    expose({ selectByName })

    return {
      rootRef, wrapperRef, svgContent, isLoading,
      mapLevel, breadcrumb, goBack, legendEntries, paletteStyle,
      markers, hoveredMarkerKey, tooltip,
      onMouseover, onMousemove, onMouseleave, onMarkerMouseleave, onMapClick,
      selectGroup, setHover, clearHover,
      activeRegionGroups, selectRegionGroup,
      regionListRef, regionListLabel, regionListTabIndexes,
      onRegionListItemFocus, onRegionListItemBlur, onRegionListKeydown,
      formatEntityLabel
    }
  }
}
